import { useRef, useState } from 'react';
import { IMAGE_MAX_BYTES, IMAGE_MIME_TYPES, type MapObject } from '@etown/shared';
import { Modal } from '../../components/Modal';
import { adminApi } from '../../net/adminApi';
import { useObjectImagesStore } from '../../stores/objectImagesStore';
import { useOffice } from '../office/OfficeContext';
import './objects.css';

/** Standard-Titel + Fallback je Objekttyp. */
const META: Record<string, { title: string; empty: string; fallback?: string }> = {
  fridge: { title: 'Kühlschrank', empty: 'Noch kein Foto hinterlegt.', fallback: '/kuehlschrank.jpg' },
  coffee: { title: 'Kaffeemaschine', empty: 'Noch kein Foto — ein Admin kann eines hochladen.' },
};

/**
 * Interaktives Bild-Objekt (Kuehlschrank, Kaffeemaschine, Fenster): zeigt das
 * hinterlegte Foto gross. Nur Admins koennen es aendern (Upload ohne
 * Naehe-Check); die Aenderung wird an alle in der Map gebroadcastet.
 */
export function ImageObjectModal({ object, onClose }: { object: MapObject; onClose: () => void }) {
  const { mapId, istAdmin: isAdmin } = useOffice();
  const imageId = useObjectImagesStore((s) => s.images[object.id]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const meta = META[object.type] ?? { title: 'Bild', empty: 'Noch kein Bild.' };
  const title = object.props.label ?? meta.title;
  const src = imageId ? `/api/images/${encodeURIComponent(imageId)}` : meta.fallback;

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    if (!(IMAGE_MIME_TYPES as readonly string[]).includes(file.type)) {
      setError('Nur PNG, JPEG oder WebP erlaubt.');
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      setError('Das Bild ist zu gross (max. 1,5 MB).');
      return;
    }
    setUploading(true);
    try {
      // object:image-Broadcast aktualisiert den Store fuer alle (inkl. uns)
      await adminApi.uploadObjectImage(mapId, object.id, file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose} wide>
      <div className="picture-modal">
        {src ? (
          <img className="picture-modal__image" src={src} alt={title} />
        ) : (
          <p className="picture-modal__empty">{meta.empty}</p>
        )}
        {error && <p className="picture-modal__error" role="alert">{error}</p>}
        {isAdmin && (
          <div className="picture-modal__actions">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => void onFile(e)}
            />
            <button className="picture-modal__upload" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? 'Lädt hoch…' : imageId ? 'Foto wechseln' : 'Foto hochladen'}
            </button>
            <span className="picture-modal__hint">PNG, JPEG oder WebP · max. 1,5 MB · nur Admin</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
